// Copy / paste of settings (§10 keys C, Shift+C, V, Shift+V; §11.5 batch sync).
// C copies everything; V pastes the last group choice (remembered) to every selected photo except the
// source, as one undo step per photo plus one global "Undo paste to N photos" entry (the toast).
import { create } from 'zustand';
import { useEditor } from './editor';
import { useLibrary } from './library';
import { applyGroups } from '../features/sync';
import { DEFAULT_SYNC_GROUPS } from '../model/defaults';
import { SYNC_GROUPS, type Settings, type SyncGroup } from '../model/types';
import type { MessageKey } from '../i18n';
import { removeBackgroundBatch } from '../features/background/matte';

const KEY = 'epb.syncGroups';
function loadGroups(): SyncGroup[] {
  try {
    const v = JSON.parse(globalThis.localStorage?.getItem(KEY) ?? 'null') as unknown;
    if (Array.isArray(v))
      return v.filter((g): g is SyncGroup => (SYNC_GROUPS as readonly string[]).includes(g));
  } catch {
    /* default below */
  }
  return [...DEFAULT_SYNC_GROUPS];
}

export type Toast = {
  key: MessageKey;
  vars?: Record<string, string | number>;
  undo?: { n: number; before: Record<string, Settings> };
};

type ClipState = {
  copied: Settings | null;
  sourceId: string | null;
  groups: SyncGroup[];
  picker: null | 'copy' | 'paste';
  toast: Toast | null;
  copy: (withPicker: boolean) => void;
  paste: (withPicker: boolean) => void;
  /** Called by the group picker. */
  confirm: (groups: SyncGroup[]) => void;
  closePicker: () => void;
  setToast: (t: Toast | null) => void;
};

/** Pastes `copied` with `groups` to `ids` (pure store operation). Returns the count pasted. */
export function pasteTo(
  ids: string[],
  copied: Settings,
  groups: SyncGroup[],
): { n: number; before: Record<string, Settings> } {
  if (!ids.length) return { n: 0, before: {} };
  const before = useEditor.getState().updateMany(ids, (s) => applyGroups(s, copied, groups));
  return { n: ids.length, before };
}

export const useClipboard = create<ClipState>((set, get) => ({
  copied: null,
  sourceId: null,
  groups: loadGroups(),
  picker: null,
  toast: null,
  copy: (withPicker) => {
    const id = useLibrary.getState().activeId;
    if (!id) return;
    set({
      copied: structuredClone(useEditor.getState().get(id)),
      sourceId: id,
      picker: withPicker ? 'copy' : null,
    });
    if (!withPicker) set({ toast: { key: 'copied' } });
  },
  paste: (withPicker) => {
    if (!get().copied) return;
    if (withPicker) {
      set({ picker: 'paste' });
      return;
    }
    doPaste();
  },
  confirm: (groups) => {
    try {
      globalThis.localStorage?.setItem(KEY, JSON.stringify(groups));
    } catch {
      /* not remembered */
    }
    const mode = get().picker;
    set({ groups, picker: null });
    if (mode === 'paste') doPaste();
    else set({ toast: { key: 'copied' } });
  },
  closePicker: () => set({ picker: null }),
  setToast: (toast) => set({ toast }),
}));

function doPaste() {
  const { copied, sourceId, groups } = useClipboard.getState();
  const lib = useLibrary.getState();
  if (!copied) return;
  const ids = [...lib.selected].filter((id) => id !== sourceId);
  const { n, before } = pasteTo(ids, copied, groups);
  if (n) useClipboard.getState().setToast({ key: 'pastedN', vars: { n }, undo: { n, before } });
  // §11.12 sync: pasting background auto queues inference for every target (cached mattes are reused).
  if (n && groups.includes('background') && copied.background.mode === 'auto')
    void removeBackgroundBatch(ids, { setMode: false });
}
